import React, { FC, useEffect } from 'react';
import { useFormik } from 'formik';
import Yup from '~yup';

import { TCreateNewBranchReduxParams } from '~types/feat/branch';
import { useAppSelector } from '~redux/hooks';

import LabelledInput from '~components/atoms/LabelledInput';
import LabelledDropdown from '~components/atoms/LabelledDropdown';
import ActionButton from '~components/atoms/ActionButton';
import Text from '~components/atoms/Text';

import { TDropdownOption } from '~types/components/labelledDropdown';
import UtilFunctions from '~utils/functions';

import * as FormStyles from './_branchBaseForm.module.scss';

type TBranchBaseForm = {
    submitButtonLabel: string;
    onSubmit: (data: TCreateNewBranchReduxParams) => void;
    error: string;
    actionButtonIsLoading?: boolean;
    actionButtonClassName?: string;
}

const BranchBaseForm: FC<TBranchBaseForm> = ({
    submitButtonLabel,
    onSubmit,
    error,
    actionButtonIsLoading,
    actionButtonClassName,
}) => {
    const { Misc: { blockEnterKey } } = UtilFunctions;
    const regions = useAppSelector(state => state.branch.allRegions);
    const regionIds = regions.map(region => region.regionId);
    const regionNames = regions.map(region => region.regionName);

    const BranchBaseFormValidation = Yup.object().shape({
        regionId: Yup.number().oneOf(regionIds, 'Invalid region').required('Invalid Region'),
        regionName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().matchNumbersAndUppercaseAlphabetsOnly().oneOf(regionNames, 'Invalid region').required('Region Name is required'),
        branchCode: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().matchNumbersOnly().length(3).required('Branch Code is required'),
        branchName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().matchNumbersAndUppercaseAlphabetsOnly().required('Branch Name is required'),
    });

    const BranchBaseFormInitialValues: TCreateNewBranchReduxParams = {
        regionId: 0,
        regionName: '',
        branchCode: '',
        branchName: '',
    };

    const formik = useFormik({
        initialValues: BranchBaseFormInitialValues,
        validationSchema: BranchBaseFormValidation,
        onSubmit: (values) => {
            const castValues = BranchBaseFormValidation.cast(values);

            onSubmit(castValues);
        },
    });

    useEffect(() => {
        if (formik.values.regionName) {
            formik.setFieldValue('regionId', regions.find(region => region.regionName === formik.values.regionName)?.regionId);
        } else formik.setFieldValue('regionId', '');
    }, [formik.values.regionName]);

    const regionOptions = regions.map(region => {
        return {
            label: region.regionName,
            value: region.regionId,
        };
    });

    const handleOnSearchRegion = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.handleChange({ target: { name: 'regionName', value: event.target.value } });
    };

    const handleOnSelectRegion = (option: TDropdownOption) => {
        const region = regions.find((regionOption) => regionOption.regionId === option.value);

        if (!region) return;

        formik.setValues({
            ...formik.values,
            regionId: region.regionId,
            regionName: region.regionName,
        });
    };

    return (
        <form
            onSubmit={formik.handleSubmit}
            className={FormStyles.container}
            onKeyDown={blockEnterKey}
        >
            <div className={FormStyles['form-container']}>
                <LabelledInput
                    id='branchCode'
                    name='branchCode'
                    label='Branch Code* :'
                    value={formik.values.branchCode}
                    error={formik.touched.branchCode ? formik.errors.branchCode : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                />

                <LabelledInput
                    id='branchName'
                    name='branchName'
                    label='Branch Name* :'
                    value={formik.values.branchName}
                    error={formik.touched.branchName ? formik.errors.branchName : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                />

                <LabelledDropdown
                    id='regionName'
                    name='regionName'
                    label='Region* :'
                    options={regionOptions}
                    value={formik.values.regionName}
                    error={formik.touched.regionName ? formik.errors.regionName || formik.errors.regionId : undefined}
                    onChange={handleOnSearchRegion}
                    onSelectOption={handleOnSelectRegion}
                    onBlur={formik.handleBlur}
                    autoComplete='off'
                    noOptionsMessage='No region found'
                    optionSelected={!!regionOptions.find(option => option.value === formik.values.regionId)}
                />

                <div className={`${FormStyles['button-container']} ${FormStyles['span-2']}`}>
                    <div className={FormStyles['button-subcontainer']}>
                        <ActionButton
                            label={submitButtonLabel}
                            isLoading={actionButtonIsLoading}
                            type='submit'
                            disabled={actionButtonIsLoading}
                            className={actionButtonClassName}
                        />

                        <Text
                            label={error}
                            isError
                        />
                    </div>
                </div>
            </div>
        </form>
    );
};

export default BranchBaseForm;
